import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import '../styles/AdminUsers.css';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetModal, setResetModal] = useState(null); // { userId, username }
  const [newPassword, setNewPassword] = useState('');
  const [resetStatus, setResetStatus] = useState(null); // { type: 'success'|'error', message }
  const [assignModal, setAssignModal] = useState(null); // { userId, username }
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [assignStatus, setAssignStatus] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data.users);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setResetStatus({ type: 'error', message: 'Password must be at least 8 characters' });
      return;
    }
    try {
      const response = await axios.put(
        `${API_URL}/admin/users/${resetModal.userId}/password`,
        { password: newPassword }
      );
      setResetStatus({ type: 'success', message: response.data.message });
      setNewPassword('');
      setTimeout(() => {
        setResetModal(null);
        setResetStatus(null);
      }, 2000);
    } catch (err) {
      setResetStatus({ type: 'error', message: err.response?.data?.message || 'Reset failed' });
    }
  };

  const openAssignModal = async (userId, username) => {
    setAssignModal({ userId, username });
    setSelectedLeagueId('');
    setAssignStatus(null);
    try {
      const response = await axios.get(`${API_URL}/admin/leagues`);
      setLeagues(response.data.leagues);
    } catch (err) {
      setAssignStatus({ type: 'error', message: 'Failed to load leagues' });
    }
  };

  const handleAssignToLeague = async () => {
    if (!selectedLeagueId) {
      setAssignStatus({ type: 'error', message: 'Please select a league' });
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/admin/leagues/${selectedLeagueId}/assign`,
        { userId: assignModal.userId }
      );
      setAssignStatus({ type: 'success', message: response.data.message });
      fetchUsers();
      setTimeout(() => {
        setAssignModal(null);
        setAssignStatus(null);
      }, 2000);
    } catch (err) {
      setAssignStatus({ type: 'error', message: err.response?.data?.message || 'Assign failed' });
    }
  };

  const ResetPasswordButton = useCallback((params) => (
    <div className="admin-action-buttons">
      <button
        className="btn-reset-password"
        onClick={() => {
          setResetModal({ userId: params.data._id, username: params.data.username });
          setNewPassword('');
          setResetStatus(null);
        }}
      >
        Reset Password
      </button>
      <button
        className="btn-assign-league"
        onClick={() => openAssignModal(params.data._id, params.data.username)}
      >
        Assign to League
      </button>
    </div>
  ), []);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Username',
      field: 'username',
      sortable: true,
      filter: true,
      minWidth: 130,
    },
    {
      headerName: 'Join Date',
      field: 'createdAt',
      sortable: true,
      filter: true,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : '—',
      minWidth: 110,
    },
    {
      headerName: 'Leagues',
      valueGetter: (params) =>
        params.data.leagues?.map((l) => l.name).join(', ') || 'None',
      sortable: true,
      filter: true,
      minWidth: 180,
    },
    {
      headerName: 'Commissioner Of',
      valueGetter: (params) =>
        params.data.commissionerOf?.map((l) => l.name).join(', ') || 'None',
      sortable: true,
      filter: true,
      minWidth: 180,
    },
    {
      headerName: 'Admin',
      field: 'isSiteAdmin',
      sortable: true,
      filter: true,
      valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
      maxWidth: 90,
    },
    {
      headerName: 'Actions',
      cellRenderer: ResetPasswordButton,
      sortable: false,
      filter: false,
      minWidth: 260,
    },
  ], [ResetPasswordButton]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    flex: 1,
  }), []);

  if (loading) return <div className="admin-loading">Loading users...</div>;
  if (error) return <div className="admin-error">{error}</div>;

  return (
    <div className="admin-users-section">
      <div className="admin-users-header">
        <h3>🛡️ Site Admin — All Users ({users.length})</h3>
      </div>
      <div className="ag-theme-alpine admin-grid">
        <AgGridReact
          rowData={users}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={10}
          animateRows={true}
        />
      </div>

      {resetModal && (
        <div className="modal-overlay" onClick={() => setResetModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Reset Password for "{resetModal.username}"</h4>
            <input
              type="password"
              placeholder="Enter new password (min 6 chars)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="modal-input"
            />
            {resetStatus && (
              <p className={`modal-status ${resetStatus.type}`}>{resetStatus.message}</p>
            )}
            <div className="modal-buttons">
              <button className="btn btn-primary" onClick={handleResetPassword}>
                Reset Password
              </button>
              <button className="btn btn-cancel" onClick={() => setResetModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {assignModal && (
        <div className="modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Assign "{assignModal.username}" to a League</h4>
            <select
              className="modal-input"
              value={selectedLeagueId}
              onChange={(e) => setSelectedLeagueId(e.target.value)}
            >
              <option value="">— Select a league —</option>
              {leagues.map((league) => (
                <option key={league._id} value={league._id}>
                  {league.name} ({league.managers.length}/{league.maxManagers} managers)
                </option>
              ))}
            </select>
            {assignStatus && (
              <p className={`modal-status ${assignStatus.type}`}>{assignStatus.message}</p>
            )}
            <div className="modal-buttons">
              <button className="btn btn-primary" onClick={handleAssignToLeague}>
                Assign
              </button>
              <button className="btn btn-cancel" onClick={() => setAssignModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
